import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import uploadFiles from "../artistStudio/studioUpload";
import { ACCENT } from "../artistStudio/shared";

const DEFAULT_MAX_FILES = 10;
const MAX_BYTES = 10 * 1024 * 1024; // matches the upload service's 10 MB limit
const TYPES = ["application/pdf", "image/jpeg", "image/png"];

// Evidence picker: PDF, JPG or PNG up to 10 MB each. Files upload as they are chosen; the form receives
// [{ url, name, type }] and sends those links to the API. `maxFiles` sets the limit (10 by default; an appeal allows 5) and
// `includeSize` adds the size in bytes to each item for forms whose API takes it (off by default: the rights-claim API rejects
// keys it does not know).
export default function EvidenceUploader({ value, onChange, disabled, error, maxFiles = DEFAULT_MAX_FILES, includeSize = false }) {
  const MAX_FILES = maxFiles;
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;
    if (value.length + picked.length > MAX_FILES) {
      toast.error(`You can add up to ${MAX_FILES} files.`);
      return;
    }
    const bad = picked.find((f) => !TYPES.includes(f.type) || f.size > MAX_BYTES);
    if (bad) {
      toast.error(TYPES.includes(bad.type) ? `${bad.name} is larger than 10 MB.` : `${bad.name}: only PDF, JPG or PNG files can be added.`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadFiles(picked);
      onChange([...value, ...uploaded.map((u, i) => ({
        url: u.url, name: picked[i].name, type: picked[i].type, ...(includeSize ? { size: picked[i].size } : {}),
      }))]);
    } catch (e) {
      toast.error(e.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <input ref={inputRef} type="file" multiple accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={onPick} data-testid="evidence-input" />
      <button
        type="button" disabled={disabled || uploading || value.length >= MAX_FILES} onClick={() => inputRef.current?.click()}
        className="rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
        style={{ color: ACCENT }}
      >
        {uploading ? "Uploading…" : "Add files"}
      </button>
      <span className="ml-3 text-xs text-gray-500">PDF, JPG or PNG, up to 10 MB each, {MAX_FILES} files at most.</span>
      {value.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-200" data-testid="evidence-list">
          {value.map((file, index) => (
            <li key={`${file.url}-${index}`} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
              <a href={file.url} target="_blank" rel="noreferrer" className="min-w-0 truncate font-medium hover:underline">{file.name || file.url}</a>
              <button type="button" onClick={() => onChange(value.filter((_, i) => i !== index))} className="shrink-0 text-gray-500 underline" aria-label={`Remove ${file.name || "file"}`}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
